import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
  Add,
  Upload,
  Delete,
  Image as ImageIcon,
  Store,
  Receipt,
  TrendingUp,
  CompareArrows,
  Refresh,
  Replay,
  Search,
  Clear,
  ShoppingCart,
} from '@mui/icons-material';
import { billsAPI, Bill, PriceAnalysis, ProductPriceComparison, PriceHistoryEntry, PriceHistoryResponse } from '../services/api';

const BillsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [purchaseDate, setPurchaseDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [billDetailOpen, setBillDetailOpen] = useState(false);
  const [priceAnalysisOpen, setPriceAnalysisOpen] = useState(false);
  const [priceAnalysis, setPriceAnalysis] = useState<PriceAnalysis | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [reprocessing, setReprocessing] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    store_id: '',
  });
  
  // Price History states
  const [priceHistoryData, setPriceHistoryData] = useState<PriceHistoryResponse | null>(null);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);
  const [productId, setProductId] = useState<number | ''>('');
  const [storeId, setStoreId] = useState<number | ''>('');
  const [days, setDays] = useState<number>(30);
  const [search, setSearch] = useState<string>('');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [priceHistoryPage, setPriceHistoryPage] = useState(0);
  const [priceHistoryRowsPerPage, setPriceHistoryRowsPerPage] = useState(50);

  useEffect(() => {
    if (activeTab === 0) {
      fetchBills();
    }
  }, [filters, activeTab]);

  useEffect(() => {
    if (activeTab === 1) {
      loadPriceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, priceHistoryPage, priceHistoryRowsPerPage, productId, storeId, days, search, minPrice, maxPrice, startDate, endDate]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (filters.status) params.status = filters.status;
      if (filters.store_id) params.store_id = parseInt(filters.store_id);
      
      const response = await billsAPI.getAll(params);
      setBills(response.data?.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bills');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      // Validate file type - enhanced for iOS and Android compatibility (matching CollectionsPage)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      
      // iOS sometimes sends empty or incorrect MIME types
      let fileType = file.type;
      if (!fileType || fileType === '') {
        // Try to determine type from filename for iOS/mobile
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
          fileType = 'image/jpeg';
        } else if (fileName.endsWith('.png')) {
          fileType = 'image/png';
        } else if (fileName.endsWith('.gif')) {
          fileType = 'image/gif';
        } else if (fileName.endsWith('.webp')) {
          fileType = 'image/webp';
        } else if (fileName.endsWith('.heic')) {
          fileType = 'image/heic';
        } else if (fileName.endsWith('.heif')) {
          fileType = 'image/heif';
        }
      }
      
      // More lenient validation for mobile - check file extension as fallback
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isValidExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(fileExtension || '');
      const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
      
      if (!allowedTypes.includes(fileType) && !isValidExtension && !isMobile) {
        console.warn('File validation failed:', { 
          fileName: file.name, 
          fileType: file.type, 
          fileSize: file.size,
          userAgent: navigator.userAgent
        });
        setError('Please select a valid image file (JPG, JPEG, PNG, GIF, WebP, HEIC, or HEIF)');
        return;
      }
      
      // For mobile devices, be more lenient - if it's an image-like file, allow it
      if (isMobile && !fileType && !isValidExtension) {
        console.warn('Unknown file type on mobile, allowing:', file.name);
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size too large. Maximum size allowed is 10MB');
        return;
      }
      
      // Validate file is not empty
      if (file.size === 0) {
        setError('Selected file is empty. Please choose a valid image.');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      console.log('Starting upload:', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        purchaseDate
      });
      
      // Compress image before upload (matching Collections page behavior)
      const isPdf = selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf');
      let fileToUpload = selectedFile;
      
      if (!isPdf) {
        try {
          const { compressImage } = await import('../services/imageCompression');
          fileToUpload = await compressImage(selectedFile, {
            maxWidth: 1600,
            maxHeight: 1600,
            quality: 0.8,
            outputType: 'image/jpeg'
          });
          console.log('Image compressed:', {
            originalSize: selectedFile.size,
            compressedSize: fileToUpload.size
          });
        } catch (compressErr) {
          console.warn('Image compression failed, uploading original:', compressErr);
          // Continue with original file if compression fails
        }
      }
      
      const response = await billsAPI.upload(fileToUpload, purchaseDate);
      
      console.log('Upload successful:', response);
      
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setPurchaseDate(new Date().toISOString().split('T')[0]);
      await fetchBills();
    } catch (err: any) {
      console.error('Upload failed:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Handle network errors specifically
      let errorMessage = 'Failed to upload bill. Please try again.';
      
      if (err.networkError) {
        // Network error from interceptor
        errorMessage = err.networkError;
      } else if (!err.response) {
        // No response from server - network error
        if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage = 'Upload timeout. The file may be too large or your connection is slow. Please try again.';
        } else if (err.code === 'ERR_INTERNET_DISCONNECTED') {
          errorMessage = 'No internet connection. Please check your network and try again.';
        } else {
          errorMessage = 'Unable to reach server. Please check your connection and try again.';
        }
      } else if (err.response?.status === 413) {
        errorMessage = 'File too large. Maximum size allowed is 10MB.';
      } else if (err.response?.status === 504 || err.response?.status === 408) {
        errorMessage = 'Upload timeout. The file may be too large or your connection is slow. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;

    try {
      await billsAPI.delete(id);
      fetchBills();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bill');
    }
  };

  const handleViewBill = (bill: Bill) => {
    setSelectedBill(bill);
    setBillDetailOpen(true);
  };

  const handlePriceAnalysis = async (productId: number, storeId?: number) => {
    try {
      setLoadingAnalysis(true);
      const response = await billsAPI.getPriceAnalysis(productId, storeId, 30);
      setPriceAnalysis(response.data?.data);
      setPriceAnalysisOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get price analysis');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleReprocessOCR = async (billId: number) => {
    if (!window.confirm('Reprocess OCR for this bill? This will extract data from the image again.')) return;

    try {
      setReprocessing(billId);
      setError(null);
      await billsAPI.reprocessOCR(billId);
      await fetchBills();
      if (selectedBill && selectedBill.id === billId) {
        // Refresh the selected bill if it's the one being reprocessed
        const response = await billsAPI.get(billId);
        setSelectedBill(response.data?.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reprocess OCR');
    } finally {
      setReprocessing(null);
    }
  };

  const formatCurrency = (amount: number | string): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
  };

  const loadPriceHistory = async () => {
    setLoadingPriceHistory(true);
    setError(null);
    
    try {
      const params: any = {
        page: priceHistoryPage + 1,
        per_page: priceHistoryRowsPerPage,
        days,
      };
      
      if (productId) params.product_id = productId;
      if (storeId) params.store_id = storeId;
      if (search) params.search = search;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await billsAPI.getPriceHistory(params);
      if (response.data.success) {
        setPriceHistoryData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load price history');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load price history');
      console.error('Error loading price history:', err);
    } finally {
      setLoadingPriceHistory(false);
    }
  };

  const handleClearPriceHistoryFilters = () => {
    setProductId('');
    setStoreId('');
    setDays(30);
    setSearch('');
    setMinPrice('');
    setMaxPrice('');
    setStartDate('');
    setEndDate('');
    setPriceHistoryPage(0);
  };

  const handlePriceHistoryPageChange = (_event: unknown, newPage: number) => {
    setPriceHistoryPage(newPage);
  };

  const handlePriceHistoryRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPriceHistoryRowsPerPage(parseInt(event.target.value, 10));
    setPriceHistoryPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bills & Receipts</Typography>
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Bill
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Bills" icon={<Receipt />} iconPosition="start" />
          <Tab label="Price History" icon={<TrendingUp />} iconPosition="start" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="processed">Processed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchBills}
                  fullWidth
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Bills List */}
          {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : bills.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No bills found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Upload your first bill to get started with price tracking
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {bills.map((bill) => (
            <Grid item xs={12} sm={6} md={4} key={bill.id}>
              <Card>
                {bill.image_url && (
                  <Box
                    sx={{
                      height: 200,
                      backgroundImage: `url(${bill.image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleViewBill(bill)}
                  />
                )}
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                    <Typography variant="h6">
                      {bill.store?.name || 'Unknown Store'}
                    </Typography>
                    <Chip
                      label={bill.status}
                      color={getStatusColor(bill.status) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(bill.purchase_date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {formatCurrency(bill.total_amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bill.items_count} item{bill.items_count !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleViewBill(bill)}>
                    View Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={reprocessing === bill.id ? <CircularProgress size={16} /> : <Replay />}
                    onClick={() => handleReprocessOCR(bill.id)}
                    disabled={reprocessing === bill.id}
                  >
                    Reprocess
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDelete(bill.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          {/* Price History Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filters & Search
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Search Products"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: search && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearch('')}>
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Product</InputLabel>
                    <Select
                      value={productId}
                      label="Product"
                      onChange={(e) => setProductId(e.target.value as number | '')}
                    >
                      <MenuItem value="">All Products</MenuItem>
                      {priceHistoryData?.filters.products.map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                          {product.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Store</InputLabel>
                    <Select
                      value={storeId}
                      label="Store"
                      onChange={(e) => setStoreId(e.target.value as number | '')}
                    >
                      <MenuItem value="">All Stores</MenuItem>
                      {priceHistoryData?.filters.stores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Period (Days)</InputLabel>
                    <Select
                      value={days}
                      label="Period (Days)"
                      onChange={(e) => setDays(e.target.value as number)}
                    >
                      <MenuItem value={7}>Last 7 days</MenuItem>
                      <MenuItem value={30}>Last 30 days</MenuItem>
                      <MenuItem value={60}>Last 60 days</MenuItem>
                      <MenuItem value={90}>Last 90 days</MenuItem>
                      <MenuItem value={180}>Last 180 days</MenuItem>
                      <MenuItem value={365}>Last year</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Min Price (₹)"
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Max Price (₹)"
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleClearPriceHistoryFilters}
                    startIcon={<Clear />}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Price History Table */}
          {loadingPriceHistory ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : priceHistoryData && (
            <>
              <Paper sx={{ mb: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Product</strong></TableCell>
                        <TableCell><strong>Store</strong></TableCell>
                        <TableCell align="right"><strong>Price</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {priceHistoryData.price_histories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No price history found matching your filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        priceHistoryData.price_histories.map((entry: PriceHistoryEntry) => (
                          <TableRow key={entry.id} hover>
                            <TableCell>{formatDate(entry.purchase_date)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShoppingCart fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {entry.product.name}
                                  </Typography>
                                  {entry.product.brand && (
                                    <Typography variant="caption" color="text.secondary">
                                      {entry.product.brand}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {entry.store ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Store fontSize="small" color="action" />
                                  <Typography variant="body2">{entry.store.name}</Typography>
                                </Box>
                              ) : (
                                <Chip label="No Store" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium" color="primary">
                                {formatCurrency(entry.price)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={priceHistoryData.pagination.total}
                  page={priceHistoryPage}
                  onPageChange={handlePriceHistoryPageChange}
                  rowsPerPage={priceHistoryRowsPerPage}
                  onRowsPerPageChange={handlePriceHistoryRowsPerPageChange}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </Paper>
              
              {/* Summary */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Showing {priceHistoryData.price_histories.length} of {priceHistoryData.pagination.total} entries
                  {priceHistoryData.pagination.total_pages > 1 && ` (Page ${priceHistoryData.pagination.page} of ${priceHistoryData.pagination.total_pages})`}
                </Typography>
              </Paper>
            </>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Bill Image</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<ImageIcon />}
              sx={{ 
                py: 1.5,
                cursor: 'pointer',
                // Ensure button is clickable on mobile
                touchAction: 'manipulation',
                width: '100%',
                mb: 2
              }}
            >
              Select Image
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    // Validate file type - enhanced for iOS and Android compatibility (matching CollectionsPage)
                    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
                    
                    // iOS sometimes sends empty or incorrect MIME types
                    let fileType = file.type;
                    if (!fileType || fileType === '') {
                      // Try to determine type from filename for iOS/mobile
                      const fileName = file.name.toLowerCase();
                      if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
                        fileType = 'image/jpeg';
                      } else if (fileName.endsWith('.png')) {
                        fileType = 'image/png';
                      } else if (fileName.endsWith('.gif')) {
                        fileType = 'image/gif';
                      } else if (fileName.endsWith('.webp')) {
                        fileType = 'image/webp';
                      } else if (fileName.endsWith('.heic')) {
                        fileType = 'image/heic';
                      } else if (fileName.endsWith('.heif')) {
                        fileType = 'image/heif';
                      }
                    }
                    
                    // More lenient validation for mobile - check file extension as fallback
                    const fileExtension = file.name.toLowerCase().split('.').pop();
                    const isValidExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(fileExtension || '');
                    const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
                    
                    if (!allowedTypes.includes(fileType) && !isValidExtension && !isMobile) {
                      console.warn('File validation failed:', { 
                        fileName: file.name, 
                        fileType: file.type, 
                        fileSize: file.size,
                        userAgent: navigator.userAgent
                      });
                      setError('Please select a valid image file (JPG, JPEG, PNG, GIF, WebP, HEIC, or HEIF)');
                      return;
                    }
                    
                    // For mobile devices, be more lenient - if it's an image-like file, allow it
                    if (isMobile && !fileType && !isValidExtension) {
                      console.warn('Unknown file type on mobile, allowing:', file.name);
                    }
                    
                    // Validate file size (10MB max)
                    if (file.size > 10 * 1024 * 1024) {
                      setError('File size too large. Maximum size allowed is 10MB');
                      return;
                    }
                    
                    // Validate file is not empty
                    if (file.size === 0) {
                      setError('Selected file is empty. Please choose a valid image.');
                      return;
                    }
                    
                    setSelectedFile(file);
                    setError(null);
                  } else {
                    setSelectedFile(null);
                  }
                }}
                onClick={(e) => {
                  // Reset value to allow selecting the same file again on mobile
                  (e.target as HTMLInputElement).value = '';
                }}
              />
            </Button>
            {selectedFile && (
              <Box sx={{ mb: 2 }}>
                <Chip label={selectedFile.name} size="small" sx={{ mb: 1 }} />
              </Box>
            )}
            {selectedFile && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 4 }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              label="Purchase Date"
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bill Detail Dialog */}
      <Dialog
        open={billDetailOpen}
        onClose={() => setBillDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bill Details
          {selectedBill && (
            <Chip
              label={selectedBill.status}
              color={getStatusColor(selectedBill.status) as any}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedBill && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Store
                  </Typography>
                  <Typography variant="body1">
                    {selectedBill.store?.name || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Purchase Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedBill.purchase_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedBill.total_amount)}
                  </Typography>
                </Grid>
              </Grid>

              {selectedBill.image_url && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={selectedBill.image_url}
                    alt="Bill"
                    style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 4 }}
                  />
                </Box>
              )}

              {selectedBill.items.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBill.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                          <TableCell align="center">
                            {item.product_id && (
                              <Button
                                size="small"
                                startIcon={<TrendingUp />}
                                onClick={() => handlePriceAnalysis(item.product_id!)}
                              >
                                Price History
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBill && (
            <Button
              startIcon={reprocessing === selectedBill.id ? <CircularProgress size={16} /> : <Replay />}
              onClick={() => handleReprocessOCR(selectedBill.id)}
              disabled={reprocessing === selectedBill.id}
              color="primary"
            >
              Reprocess OCR
            </Button>
          )}
          <Button onClick={() => setBillDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Price Analysis Dialog */}
      <Dialog
        open={priceAnalysisOpen}
        onClose={() => setPriceAnalysisOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Price Analysis (Last 30 Days)</DialogTitle>
        <DialogContent>
          {loadingAnalysis ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : priceAnalysis ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {priceAnalysis.product.name}
              </Typography>
              {priceAnalysis.store && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Store: {priceAnalysis.store.name}
                </Typography>
              )}
              <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Average Price
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(priceAnalysis.statistics.average_price)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Latest Price
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(priceAnalysis.statistics.latest_price)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Min Price
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(priceAnalysis.statistics.min_price)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Max Price
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(priceAnalysis.statistics.max_price)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              {priceAnalysis.price_history.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Store</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {priceAnalysis.price_history.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(entry.purchase_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(entry.price)}
                          </TableCell>
                          <TableCell>{entry.store_name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceAnalysisOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillsPage;
